import { useState, type FormEvent } from "react";
import PageLayout from "@/components/feature/PageLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimatedSection from "@/components/AnimatedSection";
import { footerData } from "@/mocks/homeData";

const CONTACT_FORM_URL = "https://readdy.ai/api/form/da39mfqh2m1qvvmk29og";

const projectTypes = [
  "Laravel Development",
  "Web Development",
  "Mobile App",
  "API Development",
  "SaaS",
  "AI Development",
  "Cloud & DevOps",
  "Other",
];

const budgets = ["Under $5K", "$5K–$10K", "$10K–$25K", "$25K–$50K", "$50K+", "Let's Discuss"];

const contactInfo = [
  { title: "Email", value: footerData.contact.email, icon: "ri-mail-line", href: `mailto:${footerData.contact.email}` },
  { title: "Phone", value: footerData.contact.phone, icon: "ri-phone-line", href: `tel:${footerData.contact.phone}` },
  { title: "Office Location", value: footerData.contact.address, icon: "ri-map-pin-line" },
  { title: "Working Hours", value: "Mon – Fri · 9:30 AM – 6:30 PM (IST)", icon: "ri-time-line" },
];

const trustItems = [
  { title: "Experienced Engineers", desc: "Senior Laravel and full-stack engineers on every project.", icon: "ri-user-star-line" },
  { title: "Transparent Communication", desc: "Clear updates and honest advice throughout.", icon: "ri-chat-3-line" },
  { title: "Scalable Solutions", desc: "Architecture designed to grow with your business.", icon: "ri-expand-diagonal-line" },
  { title: "Long-Term Support", desc: "We stay with you after launch.", icon: "ri-heart-3-line" },
];

export default function ContactPage() {
  const [formState, setFormState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = data.get("contact_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setFormState("success");
      form.reset();
      return;
    }
    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key !== "contact_alt" && value) payload.append(key, String(value));
    });
    setFormState("submitting");
    setFormError("");
    try {
      const res = await fetch(CONTACT_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      if (!res.ok || parsed?.code !== "OK" || isSpam) {
        setFormState("error");
        setFormError(serverMsg && String(serverMsg) !== "" ? String(serverMsg) : "Something went wrong. Please try again.");
        return;
      }
      setFormState("success");
      form.reset();
      setTouched({});
    } catch {
      setFormState("error");
      setFormError("Something went wrong. Please try again.");
    }
  };

  const inputClass = (valid: boolean) =>
    `w-full px-4 py-3 rounded-xl bg-background-50 border text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${
      valid
        ? "border-background-200/70 focus:ring-accent-500/40 focus:border-accent-400/50"
        : "border-rose-300 focus:ring-rose-400/40 focus:border-rose-400"
    }`;

  return (
    <PageLayout>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Contact Us" }]} />

        {/* Hero */}
        <section className="relative py-20 md:py-24 bg-hero-gradient overflow-hidden">
          <div className="container-astro relative z-10 text-center">
            <AnimatedSection>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse-dot" />
                <span className="text-xs font-semibold text-accent-300 tracking-wide uppercase">Get in Touch</span>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={100}>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[52px] font-extrabold text-background-50 leading-[1.1] mb-6 font-heading tracking-tight">
                Let&apos;s Build Something Great Together
              </h1>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <p className="text-base md:text-lg text-background-400 leading-relaxed mb-8 max-w-2xl mx-auto">
                Tell us about your project and our team will help you choose the right technology and approach.
              </p>
            </AnimatedSection>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background-50 to-transparent pointer-events-none" />
        </section>

        {/* Form + Contact info */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="container-astro">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
              {/* Form */}
              <div className="lg:col-span-7">
                <AnimatedSection>
                  <div className="rounded-2xl bg-background-100 border border-background-200/60 p-7 md:p-9">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-foreground-950 mb-2 font-heading tracking-tight">
                      Send an Inquiry
                    </h2>
                    <p className="text-sm text-foreground-500 mb-8">
                      Fill in the form and we'll get back to you within one business day.
                    </p>

                    {formState === "success" ? (
                      <div className="flex flex-col items-center text-center py-14">
                        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 mb-5">
                          <i className="ri-checkbox-circle-fill text-3xl" />
                        </span>
                        <h3 className="text-xl font-bold text-foreground-950 mb-2 font-heading">Message Sent!</h3>
                        <p className="text-sm text-foreground-600 max-w-sm">
                          Thank you for reaching out. Our team will review your inquiry and respond shortly.
                        </p>
                        <button
                          onClick={() => setFormState("idle")}
                          className="mt-6 px-6 py-3 rounded-xl bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/20"
                        >
                          Send Another Message
                        </button>
                      </div>
                    ) : (
                      <form data-readdy-form onSubmit={handleSubmit} noValidate>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div>
                            <label className="block text-sm font-semibold text-foreground-800 mb-2">Name *</label>
                            <input
                              type="text"
                              name="name"
                              required
                              onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                              placeholder="Your full name"
                              className={inputClass(!touched.name || true)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-foreground-800 mb-2">Company</label>
                            <input
                              type="text"
                              name="company"
                              placeholder="Company name (optional)"
                              className={inputClass(true)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-foreground-800 mb-2">Email *</label>
                            <input
                              type="email"
                              name="email"
                              required
                              placeholder="you@company.com"
                              className={inputClass(true)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-foreground-800 mb-2">Phone</label>
                            <input
                              type="tel"
                              name="phone"
                              placeholder="+1 (555) 000-0000"
                              className={inputClass(true)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-foreground-800 mb-2">Project Type</label>
                            <select name="project_type" className={inputClass(true)} defaultValue="">
                              <option value="" disabled>Select a project type</option>
                              {projectTypes.map((t) => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-foreground-800 mb-2">Budget</label>
                            <select name="budget" className={inputClass(true)} defaultValue="">
                              <option value="" disabled>Select a budget range</option>
                              {budgets.map((b) => (
                                <option key={b} value={b}>{b}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="mt-5">
                          <label className="block text-sm font-semibold text-foreground-800 mb-2">Message *</label>
                          <textarea
                            name="message"
                            required
                            rows={5}
                            maxLength={500}
                            placeholder="Tell us about your project, goals and timeline..."
                            className={`${inputClass(true)} resize-none`}
                          />
                          <p className="text-xs text-foreground-400 mt-1 text-right">Maximum 500 characters</p>
                        </div>

                        {/* Honeypot */}
                        <input
                          type="text"
                          name="contact_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                          className="hp-trap"
                        />

                        {formState === "error" && (
                          <div className="mt-5 flex items-start gap-2 rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-700">
                            <i className="ri-error-warning-line mt-0.5" />
                            <span>{formError}</span>
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={formState === "submitting"}
                          className="mt-7 inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-xl hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/25 btn-arrow-hover group"
                        >
                          {formState === "submitting" ? "Sending..." : "Send Inquiry"}
                          <i className="ri-arrow-right-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                      </form>
                    )}
                  </div>
                </AnimatedSection>
              </div>

              {/* Contact info */}
              <div className="lg:col-span-5">
                <AnimatedSection delay={100}>
                  <div className="flex flex-col gap-4 mb-6">
                    {contactInfo.map((info) => (
                      <div
                        key={info.title}
                        className="flex items-start gap-4 p-5 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/40 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300"
                      >
                        <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white shrink-0">
                          <i className={`${info.icon} text-lg`} />
                        </span>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-1">
                            {info.title}
                          </p>
                          {info.href ? (
                            <a href={info.href} className="text-sm font-semibold text-foreground-900 hover:text-accent-600 transition-colors">
                              {info.value}
                            </a>
                          ) : (
                            <p className="text-sm font-semibold text-foreground-900">{info.value}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </AnimatedSection>

                {/* Map */}
                <AnimatedSection delay={200}>
                  <div className="rounded-2xl overflow-hidden border border-background-200/60">
                    <iframe
                      title="AstroJal Technology Office Location"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3671.7263!2d72.6360!3d23.0505!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e836c1c3c3f2f%3A0x2f7e8f0f3c6d8c1b!2sFortune%20Circle%2C%20New%20Naroda%2C%20Ahmedabad%2C%20Gujarat!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                      width="100%"
                      height="320"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                </AnimatedSection>
              </div>
            </div>
          </div>
        </section>

        {/* Trust */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="container-astro">
            <AnimatedSection>
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground-950 mb-3 font-heading tracking-tight">
                  Why Start With AstroJal?
                </h2>
                <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
              </div>
            </AnimatedSection>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {trustItems.map((item, i) => (
                <AnimatedSection key={item.title} delay={i * 100}>
                  <div className="group p-6 md:p-7 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                      <span className="w-6 h-6 flex items-center justify-center">
                        <i className={`${item.icon} text-xl`} />
                      </span>
                    </div>
                    <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{item.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-cta-gradient relative overflow-hidden">
          <div className="container-astro relative z-10 text-center">
            <AnimatedSection>
              <h2 className="text-2xl md:text-4xl font-extrabold text-background-50 mb-4 font-heading tracking-tight">
                Not sure where to start?
              </h2>
              <p className="text-base text-background-400 max-w-2xl mx-auto mb-8 leading-relaxed">
                Tell us what you're trying to build and we'll help you choose the right approach.
              </p>
              <a
                href="mailto:info@astrojal.com"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-base font-semibold rounded-xl hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/25 btn-arrow-hover group"
              >
                Talk to Our Experts
                <i className="ri-arrow-right-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </AnimatedSection>
          </div>
        </section>
    </PageLayout>
  );
}